import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../ThemeContext'; // Adjust the path as necessary
import { getRFI, deleteUser, changeUserStatus } from '../../api/api';
import AddUser from '../AddUser';
import { MantineProvider } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../ConfirmationModal';
import { formatDate } from '../../api/services';

export const Example = ({ handlePageUpdate, updatePage }) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get the theme from context
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
//console.log(data)
  // Columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'project_id.project_name',
      header: 'Project Name',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'document_refrence',
      header: 'Document Reference',
      Cell: ({ row }) => row.original.document_refrence || 'N/A',
    },
    {
      accessorKey: 'reason_for_rfi',
      header: 'Reason',
    },
    {
      accessorKey: 'required_response_date',
      header: 'Deadline',
      Cell: ({ row }) => formatDate(row.original.required_response_date) || 'N/A',
    },
 
    // {
    //   accessorKey: 'suggested_solution',
    //   header: 'Suggested Solution',
    //   Cell: ({ cell }) => cell.getValue() || 'N/A',
    // },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      Cell: ({ cell }) => formatDate(cell.getValue()) || 'N/A',
    },
   
  ], []);

  // Edit row handler
  const handleEditRow = (row) => {
    setEditingRow(row.original);
    setEditing(true);
  };

  // Delete modal handler
  const openDeleteModal = (row) => {
    setRowToDelete(row.original);
    setDeleteModalOpen(true);
  };

  // Confirm delete action
  const handleDeleteConfirm = async () => {
    try {
      setData((prevData) => prevData.filter((item) => item.id !== rowToDelete.id));
      setDeleteModalOpen(false);
      setRowToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Fetch data when updatePage changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRFI();
        setData(response.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [updatePage]);

  // Initialize the Mantine React Table with custom styling
  const table = useMantineReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableRowNumbers: true,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      sx: {
        'thead > tr': {
          backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
        },
        'thead > tr > th': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
        'tbody > tr': {
          '&:hover': {
            backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
          },
        },
        'tbody > tr > td': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
          color: theme === 'dark' ? '#ffff' : '#333333',
        },
      },
    },
  });

  return (
    <MantineProvider
      theme={{
        colorScheme: theme,
        primaryColor: 'blue',
        primaryShade: 5,
      }}
    >
      <MantineReactTable table={table} />
      
      {/* Conditional Rendering for Add User Form */}
      {editing && (
        <AddUser
          isEditing={editing}
          setEditing={setEditing}
          userDetails={editingRow}
          handlePageUpdate={handlePageUpdate}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <ConfirmationModal
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          handleDelete={handleDeleteConfirm}
          message={`Are you sure you want to delete ${rowToDelete?.name || 'this item'}?`}
        />
      )}
    </MantineProvider>
  );
};

export default Example;
